import { baseUrl, encodeEntryNameForURI, getEntryUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";
import {
    BaseGenieSettings,
    BaseSettingsParams,
    CategoryData,
    CategoryType,
    GenieContextParams,
    GenieContextType,
    GetSourceUrlType,
} from "./types";
import { ChatContext } from "@unisphere/genie-types";

export function getCategoryUrl(categoryData: CategoryData | undefined): string {
    if (!categoryData) {
        return "";
    }

    const host = serverUrl + (baseUrl ? `/${baseUrl}` : "");

    switch (categoryData.type) {
        case CategoryType.CHANNEL:
            return `${host}/channel/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
        case CategoryType.GALLERY:
            return `${host}/category/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
        default:
            return "";
    }
}

/**
 * Build the base chat runtime settings shared between Genie and GenieFloater.
 * Callers can spread additional fields (genieServerUrl, analytics, etc.) on top.
 */
export function buildBaseGenieSettings({
    ks,
    partnerId,
    agentMode,
    categoryData,
    categoryUrl,
    entryData,
    mode = "chat",
    sourcePageUrl,
}: BaseSettingsParams) {
    const baseSettings: BaseGenieSettings = {
        getSourceUrl: (params?: GetSourceUrlType) => {
            const { type, categoryId, entryId, startTime } = params ?? {};
            if (entryId) {
                return getEntryUrl(entryId) + (startTime ? `?st=${startTime}` : "");
            }
            if (sourcePageUrl) {
                return sourcePageUrl;
            }
            if ((type === GenieContextType.CATEGORY || type === GenieContextType.ANCESTOR) && categoryId) {
                return `${baseUrl}/channel//${categoryId}`;
            }
            return "";
        },
        shareUrl: {
            queryParam: "mid" as const,
            createUrl: ({ messageId }: { messageId: string }) => {
                if (entryData && mode === "everywhere") {
                    return `${serverUrl}${getEntryUrl(entryData.id)}?mid=${messageId}`;
                }

                const host = serverUrl + (baseUrl ? `/${baseUrl}` : "");

                if (categoryData) {
                    if (mode === "everywhere") {
                        return `${getCategoryUrl(categoryData)}?mid=${messageId}`;
                    }
                    return `${host}/genie/index/index/categoryId/${categoryData.id}?mid=${messageId}`;
                }
                return `${host}/genie?mid=${messageId}`;
            },
        },
        ks,
        partnerId: partnerId.toString(),
        agentMode,
    };

    if (!categoryData) {
        return baseSettings;
    }

    return {
        ...baseSettings,
        customization: {
            breadcrumb: {
                root: {
                    url: categoryUrl,
                    label: categoryData.name,
                },
            },
        },
    };
}

/**
 * Build the context object used in visual settings for both Genie and GenieFloater.
 */
export function getGenieContext({
    entryData,
    categoryData,
    sourcePageUrl,
}: GenieContextParams): ChatContext | undefined {
    if (entryData) {
        const result = {
            type: GenieContextType.ENTRY,
            id: entryData.id,
            title: entryData.title,
            ...(entryData.imageUrl ? { imageUrl: entryData.imageUrl } : {}),
            ...(sourcePageUrl ? { templateName: sourcePageUrl } : {}),
        };
        return result as unknown as ChatContext;
    }
    if (categoryData) {
        const type = categoryData.isAncestorType ? GenieContextType.ANCESTOR : GenieContextType.CATEGORY;
        return {
            type,
            id: String(categoryData.id),
            title: categoryData.name,
            ...(sourcePageUrl ? { templateName: sourcePageUrl } : {}),
        } as unknown as ChatContext;
    }
    return undefined;
}
